<script setup lang="ts">
import { mdiClose, mdiFilterOutline, mdiFilterRemove, mdiLightningBoltOutline, mdiStarOutline, mdiTrophyOutline } from "@mdi/js";
import EventSmartPicks from "~/components/layout/Sport/SportsBook/EventFilters/SmartPicksModal/index.vue";
import { useBetbook } from "~/store/betbook";
import { ISport } from "~/types/sports/sport";
import t from "~/utils/translate";
import { useQuery } from "@tanstack/vue-query";
import api from "~/utils/auth/api";
import { IMarketHeader } from "~/types/config/marketHeader";
import Skeleton from "~/components/ui/Skeleton.vue";
import { useSettings } from "~/store/settings";
import Dropdown from "~/components/ui/Form/Dropdown.vue";
import { totalGoalMarkets } from "~/utils/constants";
import UseCollapsedView from "~/composables/useCollapsedView";
import FilterButtons from "~/components/layout/Sport/SportsBook/EventFilters/FilterButtons.vue";
import { isMobile } from "~/utils/constants";
import MarketSlider from "~/components/ui/Markets/Slider.vue";

interface Props {
	sport: ISport | undefined;
	isLoading: boolean;
	fixtureType: string;
}

const { $trackEvent } = useNuxtApp();
const props = defineProps<Props>();
const betbook = useBetbook();
const route = useRoute();
const router = useRouter();
const smartPicks = ref(false);
const settings = useSettings();
const isCollapsedView = UseCollapsedView();

const filteredMarkets = computed(() => {
	const keys = betbook.displayMarkets.map((x) => x.name);
	return props.sport && props.sport.filterMarkets.filter((m) => !keys.includes(m.name));
});

const handleMarketChange = (val: { name: string; displayName: string; filterIndex: number } | string, index: number) => {
	if (typeof val !== "string") {
		const clone = [...betbook.displayMarkets];
		clone[index] = val;
		betbook.displayMarkets = clone;
		$trackEvent.toggleFixtureType(val.displayName);
		scrollIntoView();
	}
};

const isTotalGoals = (index: number) => {
	return index !== undefined ? !!totalGoalMarkets[betbook.displayMarkets[index || 0].name] : false;
};

const activeFilterButton = tw`rounded border border-dark-600 bg-dark-600 gap-1 font-bold text-light-50 hover:bg-dark-600 flex items-center transition-all  dark:hover:bg-dark-500 justify-center min-w-[32px] h-full cursor-pointer text-xs px-2`;
const activeFilterButtonSmall = tw`rounded border border-dark-600 bg-dark-600 gap-1 font-bold text-light-50 hover:bg-dark-600 flex items-center transition-all  dark:hover:bg-dark-500 justify-center min-w-[32px] h-7 cursor-pointer text-xs`;
const filterButton = tw`rounded border border-light-500 gap-1 font-bold hover:bg-light-500 flex items-center transition-all dark:border-dark-500 dark:hover:bg-dark-500 justify-center min-w-[32px] h-full cursor-pointer text-xs px-2`;
const filterButtonSmall = tw`rounded border border-light-500 gap-1 font-bold hover:bg-light-500 flex items-center transition-all dark:border-dark-500 dark:hover:bg-dark-500 justify-center min-w-[28px] h-7 cursor-pointer text-xs`;
const filterButtonIcon = tw`w-5 h-5 dark:fill-light-50 fill-dark-700 shrink-0`;

const leagueApplied = computed(() => {
	return (!props.fixtureType || props.fixtureType === "highlights") && !!betbook.filters.regionAndLeagueIds;
});

const filterApplied = computed(
	() =>
		(!props.fixtureType || props.fixtureType === "highlights") &&
		(betbook.filters.sortOrder !== "League" ||
			!!betbook.filters.regionAndLeagueIds ||
			!!betbook.filters.fromStartEpoch ||
			!!betbook.filters.toStartEpoch ||
			!!betbook.filters.odds)
);

const handleResetFilters = () => {
	betbook.filters.sortOrder = "League";
	betbook.filters.regionAndLeagueIds = undefined;
	betbook.filters.fromStartEpoch = undefined;
	betbook.filters.toStartEpoch = undefined;
	betbook.filters.odds = undefined;
	router.push(route.path);
};
// Market headers
const shouldFetch = computed(() => !!props.sport);
const sportId = computed(() => props.sport?.sportId || "soccer");
const { data: headers, isLoading: headersLoading } = useQuery<{ sport: string; sportMarkets: IMarketHeader[] }>({
	queryKey: ["market-headers", sportId],
	queryFn: () =>
		api({
			url: `/cron/sports-book/market-header-config/{app}/{brand}/${sportId.value}`,
			type: "config",
			options: {
				method: "GET",
			},
		}),
	enabled: shouldFetch,
});

const getMarketHeaders = (market: string) => {
	return headers.value?.sportMarkets.filter((x) => x.market === market)[0];
};

const isMarketActive = (marketName: string) => {
	return !!betbook.displayMarkets.find((x) => x.name === marketName);
};

const handleBoostedOnly = () => {
	if (betbook.filters.boostedOnly === false && !betbook.displayMarkets.find((x) => x.name === betbook.filters.markets[0].name)) {
		betbook.displayMarkets[0] = betbook.filters.markets[0];
		scrollIntoView();
	}
	betbook.filters.boostedOnly = !betbook.filters.boostedOnly;
};

const scrollIntoView = async () => {
	setTimeout(() => {
		const activeTab = document.getElementById("sport-market-list-scroller")?.getElementsByClassName("sport-market-list-active")[0];
		if (activeTab) {
			activeTab.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
		}
	}, 100);
};

const marketOptions = computed(() => {
	return filteredMarkets.value?.map((x) => {
		return {
			name: totalGoalMarkets[x.name] ? t("total-goals") : x.displayName,
			value: {
				...x,
			},
		};
	});
});
</script>

<template>
	<!-- Mobile Market Filters -->
	<div
		:class="[
			'flex flex-col gap-1 overflow-hidden sticky left-0 z-10 bg-light-200 dark:bg-dark-900 lg:hidden pb-1 -mb-1',
			settings.isNativeApp
				? isCollapsedView
					? 'top-[48px] pt-[5px]'
					: 'top-[64px]'
				: isCollapsedView
				? settings.isDataFree
					? 'top-[148px] left-0 pt-1'
					: 'top-[108px] left-0 pt-1'
				: settings.isDataFree
				? 'top-[216px] md:top-[260px] lg:top-[304px]'
				: 'top-[176px] md:top-[220px] lg:top-[264px]',
		]"
	>
		<div v-if="isLoading" class="flex items-center gap-1 overflow-x-scroll overflow-y-hidden scrollbar-hidden">
			<div
				v-for="(loader, index) in Array.from({ length: 6 }).keys()"
				:key="loader"
				class="h-[26px] w-[65px] shrink-0 items-center flex border-b-2 justify-center"
				:class="index === 0 ? 'border-b-identity' : 'border-b-[transparent]'"
			>
				<Skeleton class="w-[60px] h-[13px] bg-light-500 dark:bg-dark-300"></Skeleton>
			</div>
		</div>
		<MarketSlider v-else />
		<div class="grid grid-cols-12 pr-1 mt-2" :class="isLoading && 'opacity-50 pointer-events-none'">
			<FilterButtons :fixture-type="fixtureType" @boosted-only="handleBoostedOnly()" />
			<div class="flex items-end col-span-6 gap-1 text-xs font-bold leading-5 text-dark-700 dark:text-light-600">
				<div class="flex flex-1" v-if="betbook.displayMarkets.length && totalGoalMarkets[betbook.displayMarkets[0].name] && isMobile" />
				<div
					v-if="headers && headers.sportMarkets"
					v-for="header in getMarketHeaders(betbook.displayMarkets[0].name)?.marketOutcome"
					:key="header.outcomeKey"
					class="flex justify-center flex-1"
				>
					{{ t(header.outcomeKey, { identifier: "market-headings." }) }}
				</div>
			</div>
		</div>
	</div>
	<!-- Desktop Market Filters -->

	<div v-if="isLoading" class="flex-col hidden gap-1 lg:flex">
		<div class="grid w-full h-10 grid-cols-11 p-1 rounded-lg bg-light-50 dark:bg-dark-800"></div>
		<div class="w-full h-[16px]" />
	</div>
	<div
		v-else
		class="lg:flex flex-col gap-1 sticky z-10 bg-light-200 dark:bg-dark-900 hidden"
		:class="isCollapsedView ? 'top-0 left-0' : settings.isDataFree ? 'top-[216px] lg:top-[304px] left-0' : 'top-[176px] lg:top-[264px] left-0'"
	>
		<div class="flex w-full gap-1 p-1 rounded-lg md:grid md:grid-cols-11 bg-light-50 dark:bg-dark-800">
			<FilterButtons :fixture-type="fixtureType" @boosted-only="handleBoostedOnly()" />
			<div class="flex flex-1 gap-3 md:col-span-6">
				<Dropdown
					size="medium"
					class="flex flex-1 text-xs font-bold"
					:model-value="betbook.displayMarkets[index].name"
					@update:model-value="(val) => handleMarketChange(val, index)"
					label=""
					v-for="(item, index) in betbook.displayMarkets"
					:options="[
						{
							name: totalGoalMarkets[betbook.displayMarkets[index].name] ? t('total-goals') : betbook.displayMarkets[index].displayName,
							value: betbook.displayMarkets[index].name,
						},
						...marketOptions,
					]"
					:class="`event-market-select-${index}`"
					:key="item.name"
					no-floating-label
				/>
			</div>
		</div>
		<div class="grid w-full grid-cols-11 py-1">
			<div class="flex col-span-5">
				<div
					v-if="filterApplied"
					@click="handleResetFilters"
					class="flex flex-grow-0 items-center gap-[2px] text-xs cursor-pointer pl-1 pr-2 h-6 bg-light-50 dark:bg-dark-600 dark:hover:bg-dark-500 rounded font-bold hover:bg-light-500 transition-all"
				>
					<UiIcon :path="mdiClose" class="w-4 h-4 fill-[currentColor]" />
					{{ t("clear-filters") }}
				</div>
			</div>
			<div class="flex col-span-6 gap-3 text-xs font-bold leading-3 text-dark-700 dark:text-light-600 h-[14px]">
				<div
					v-for="(item, index) in betbook.displayMarkets"
					:key="item.name"
					v-if="!headersLoading"
					class="flex items-center flex-1 justify-evenly"
					:class="isTotalGoals(index) ? `event-market-total-goals-${index}` : `event-market-${index}`"
				>
					<div
						v-if="headers && headers.sportMarkets"
						v-for="header in getMarketHeaders(item.name)?.marketOutcome"
						:key="header.outcomeKey"
						:class="['flex justify-center flex-1', { 'col-start-2': header.outcomeKey === 'over' }]"
					>
						{{ t(header.outcomeKey, { identifier: "market-headings." }) }}
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<style scoped lang="scss">
.event-market-select-1,
.event-market-heading-1 {
	@apply hidden md:flex;
}

.event-market-select-2,
.event-market-heading-2 {
	@apply hidden lg:flex;
}

.event-market-total-goals-1 {
	@apply hidden md:grid grid-cols-3;
}
.event-market-total-goals-2 {
	@apply hidden lg:grid grid-cols-3;
}
</style>
